import { Inject, Injectable } from "@angular/core";
import * as agreementActions from "@bca-agreement/store/agreement.actions";
import * as companyDataActions from "@bca-company-data/store/company-data.actions";
import * as composeActions from "@bca-compose/store/compose.actions";
import * as confirmationActions from "@bca-confirmation/store/confirmation.actions";
import { Language } from "@bca-core/models/_enums";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { RouterService } from "@bca-core/services/router.service";
import { SentryService } from "@bca-core/services/sentry.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  MessageType,
  Route,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { IMessage } from "@bca-shared/models/message";
import * as appActions from "@bca-store/app.actions";
import { AppActionTypes } from "@bca-store/app.actions";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IStorageErrorEvent } from "@bca-store/models/storage-error";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { IRootState } from "@bca/store/store.reducers";
import { environment } from "@environments/environment";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { fromEvent } from "rxjs";
import {
  bufferTime,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { IAction } from "./models/action";

@Injectable()
export class AppEffects {
  setDefaultsOnRoute = createEffect(() =>
    this._actions.pipe(
      ofType<appActions.SetRoute>(AppActionTypes.SetRoute),
      withLatestFrom(
        this._store$.pipe(select(({ receipt }) => receipt.products)),
        this._store$.pipe(select(({ compose }) => compose.selectedProduct)),
      ),
      mergeMap(([action, products, selectedProduct]) => {
        const { route } = action.payload;

        const dispatch: IAction[] = [
          this._createAction(new appActions.DeleteMessage()),
        ];

        if (route < Route.VERIFY) {
          dispatch.push(
            this._createAction(
              new receiptActions.SetVerifiedStatus({ verified: false }),
            ),
          );
        }

        switch (route) {
          case Route.USER_DATA:
          case Route.COMPANY_DATA:
          case Route.VERIFY: {
            /**
             * only here to ensure the selected product is inside receipt.
             * sometimes when the user presses the back button in the browser it'll remove
             * the selected product from receipt and causes an error
             */
            if (
              selectedProduct &&
              !products.some((product) => product.id === selectedProduct.id)
            ) {
              dispatch.push(
                this._createAction(
                  new receiptActions.AddProduct(selectedProduct),
                ),
                this._createAction(new composeActions.SelectProduct(null)),
              );
            }
            break;
          }
        }

        return dispatch;
      }),
    ),
  );

  setHighestRoute = createEffect(() =>
    this._actions.pipe(
      ofType(AppActionTypes.SetRoute),
      withLatestFrom(this._store$.pipe(select(({ app }) => app.highestRoute))),
      filter(([action, highestRoute]) => {
        const { route } = (action as appActions.SetRoute).payload;
        return route >= highestRoute && route < Route.AGREEMENT;
      }),
      map(([action]) => {
        const { route } = (action as appActions.SetRoute).payload;
        return this._createAction(new appActions.SetHighestRoute({ route }));
      }),
    ),
  );

  scrollToTop = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.ScrollToTop),
        tap(() =>
          this._page.scrollTo(ScrollPositionId.TOP, {
            timeout: 275,
            scrollOffset: 250,
          }),
        ),
      ),
    { dispatch: false },
  );

  resetState = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.ResetState),
        tap(() => {
          try {
            const { nsBusinessUrl, nsBusinessUrlEnglish } = environment;
            sessionStorage.clear();
            this._routerService.redirectToUrl(
              this._translate.currentLang === Language.DUTCH
                ? nsBusinessUrl
                : nsBusinessUrlEnglish,
            );
          } catch (error) {
            const message = {
              body: "NOTIFICATION.ERROR.SESSIONSTORAGE",
              type: MessageType.ERROR,
              context: { error, sessionStorage },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message)),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  stopAllLoading = createEffect(() =>
    this._actions.pipe(
      ofType(AppActionTypes.StopAllLoading),
      mergeMap(() => {
        const loading = false;
        return this._createActions([
          new composeActions.ProductsLoading({ loading }),
          new composeActions.RouteLoading({ loading }),
          new composeActions.StationsLoading({ loading }),
          new userDataActions.MiddleNamesLoading({ loading }),
          new userDataActions.AddressLoading({ loading }),
          new userDataActions.ImageUploading({ loading }),
          new companyDataActions.CompanyLoading({ loading }),
          new companyDataActions.CompanyFetching({ fetching: loading }),
          new agreementActions.AgreementPreviewLoading({ loading }),
          new confirmationActions.StartPaymentLoading({ loading }),
          new confirmationActions.StatusPaymentLoading({ loading }),
        ]);
      }),
    ),
  );

  downloadApplicationForm = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.DownloadApplicationForm),
        tap(() => {
          let url: string;

          if (this._translate.currentLang === "nl") {
            url = this._config.application_form_nl_url;
          } else {
            url = this._config.application_form_eng_url;
          }

          window.open(url, "_blank").focus();
        }),
      ),
    { dispatch: false },
  );

  nextRoute = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.NextRoute),
        tap(() => this._routerService.nextRoute()),
      ),
    { dispatch: false },
  );

  previousRoute = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.PreviousRoute),
        tap(() => this._routerService.previousRoute()),
      ),
    { dispatch: false },
  );

  scrollToTopOnNewMessage = createEffect(() =>
    this._actions.pipe(
      ofType(AppActionTypes.SetMessage),
      filter((action: appActions.SetMessage) => action.payload != null),
      map((action: appActions.SetMessage) => action.payload),
      filter(({ type }) => type < MessageType.DEBUG),
      map(() => this._createAction(new appActions.ScrollToTop())),
    ),
  );

  stopLoadingOnMessage = createEffect(() =>
    this._actions.pipe(
      ofType(AppActionTypes.SetMessage),
      filter((action: appActions.SetMessage) => action.payload != null),
      map(() => this._createAction(new appActions.StopAllLoading())),
    ),
  );

  hideDialogsOnMessage = createEffect(() =>
    this._actions.pipe(
      ofType(AppActionTypes.SetMessage),
      filter((action: appActions.SetMessage) => action.payload != null),
      map(() => this._createAction(new appActions.CloseDialogs())),
    ),
  );

  closeDialogs = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.CloseDialogs),
        tap(() => this._dialogService.closeAll()),
      ),
    { dispatch: false },
  );

  logToSentryOnNewMessage = createEffect(
    () =>
      this._actions.pipe(
        ofType(AppActionTypes.SetMessage),
        filter((action: appActions.SetMessage) => action.payload != null),
        map((action: appActions.SetMessage) => action.payload),
        switchMap((message: IMessage) => {
          return this._translate.get(message.body).pipe(
            map((body: string) => {
              return { ...message, body };
            }),
          );
        }),
        tap((message: IMessage) => {
          const { body: msg, context, type, originalException } = message;
          switch (type) {
            case MessageType.ERROR:
              this._sentry.logError(originalException || msg, {
                ...context,
                msg,
              });
              break;
            case MessageType.INFO:
              this._sentry.logInfo(msg, context);
              break;
            case MessageType.DEBUG:
              this._sentry.logDebug(msg, context);
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  storageError = createEffect(() =>
    fromEvent(window, "storageError").pipe(
      bufferTime(500),
      filter((events) => events.length > 0),
      map((events) => events.pop() as IStorageErrorEvent),
      map(({ error }) => {
        const message = {
          body: "NOTIFICATION.ERROR.SESSIONSTORAGE",
          type: MessageType.ERROR,
          context: { error },
          originalException: error,
        };
        return this._createAction(new appActions.SetMessage(message));
      }),
    ),
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, AppEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, AppEffects);
  }

  constructor(
    @Inject(APP_CONFIG) private _config: IAppConfiguration,
    private _actions: Actions,
    private _translate: TranslateService,
    private _routerService: RouterService,
    private _store$: Store<IRootState>,
    private _page: PageScrollService,
    private _dialogService: DialogService,
    private _sentry: SentryService,
  ) {}
}
